import {
  cloneElement,
  type FocusEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/shared/lib/cn";
import { QuestionIcon } from "./icons";
import { InlineAction } from "./Actions";

/** Optional field guidance, available on hover and keyboard focus. */
export function HelpTooltip({ label, content }: { label: string; content: ReactNode }) {
  return <Tooltip content={content} align="start"><InlineAction aria-label={label} className="studio-help-trigger"><QuestionIcon /></InlineAction></Tooltip>;
}

const TOOLTIP_ACTIVATE_EVENT = "afterform:tooltip-activate";

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement<{
    "aria-describedby"?: string;
    "aria-expanded"?: boolean;
  }>;
  side?: "top" | "bottom";
  align?: "center" | "start" | "end";
  className?: string;
  triggerClassName?: string;
  /** Set false when the tooltip repeats an existing accessible name. */
  describe?: boolean;
}

/** Non-interactive tooltip with a 300ms entrance delay and no arrow. */
export function Tooltip({
  content,
  children,
  side = "bottom",
  align = "center",
  className,
  triggerClassName,
  describe = true,
}: TooltipProps) {
  const id = useId();
  const [active, setActive] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number; direction: "rtl" | "ltr" }>();
  const describedBy = [children.props["aria-describedby"], id]
    .filter(Boolean)
    .join(" ");
  const expanded = children.props["aria-expanded"] === true;

  useLayoutEffect(() => {
    if (!active || expanded) return;
    const measure = () => {
      const anchor = anchorRef.current;
      const tooltip = tooltipRef.current;
      if (!anchor || !tooltip) return;
      const rect = anchor.getBoundingClientRect();
      const { width, height } = tooltip.getBoundingClientRect();
      const edge = 8, gap = 6;
      const direction = getComputedStyle(anchor).direction === "rtl" ? "rtl" : "ltr";
      const rightAligned = (align === "end") !== (direction === "rtl");
      const desiredLeft = align === "center" ? rect.left + (rect.width - width) / 2 : rightAligned ? rect.right - width : rect.left;
      const above = rect.top - gap - height;
      const below = rect.bottom + gap;
      const useTop = side === "top" ? above >= edge || below + height > innerHeight - edge : below + height > innerHeight - edge && above >= edge;
      setPosition({
        left: Math.max(edge, Math.min(desiredLeft, innerWidth - width - edge)),
        top: Math.max(edge, Math.min(useTop ? above : below, innerHeight - height - edge)),
        direction,
      });
    };
    const dismiss = (event: KeyboardEvent) => { if (event.key === "Escape") setActive(false); };
    measure();
    const observer = new ResizeObserver(measure);
    if (anchorRef.current) observer.observe(anchorRef.current);
    if (tooltipRef.current) observer.observe(tooltipRef.current);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("keydown", dismiss);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("keydown", dismiss);
    };
  }, [active, expanded, side, align, content]);

  useEffect(() => {
    const dismissForAnotherTooltip = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== id) setActive(false);
    };
    window.addEventListener(TOOLTIP_ACTIVATE_EVENT, dismissForAnotherTooltip);
    return () =>
      window.removeEventListener(
        TOOLTIP_ACTIVATE_EVENT,
        dismissForAnotherTooltip,
      );
  }, [id]);

  const activate = () => {
    setActive(true);
    window.dispatchEvent(
      new CustomEvent<string>(TOOLTIP_ACTIVATE_EVENT, { detail: id }),
    );
  };
  const deactivateAfterBlur = (event: FocusEvent<HTMLSpanElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setActive(false);
  };

  return (
    <span
      ref={anchorRef}
      className={cn("relative inline-flex", triggerClassName)}
      onMouseEnter={activate}
      onMouseLeave={() => setActive(false)}
      onFocusCapture={activate}
      onBlurCapture={deactivateAfterBlur}
      onPointerDownCapture={() => setActive(false)}
    >
      {describe
        ? cloneElement(children, { "aria-describedby": describedBy })
        : children}
      {typeof document !== "undefined" ? createPortal(<span
        ref={tooltipRef}
        id={id}
        role="tooltip"
        aria-hidden={!describe || !active || expanded ? true : undefined}
        hidden={expanded}
        style={{ ...position, visibility: active && !expanded && position ? undefined : "hidden" }}
        className={cn(
          "pointer-events-none fixed z-[110] w-max max-w-[min(240px,calc(100vw-16px))] whitespace-normal break-words rounded-[7px] bg-ink px-[10px] py-1.5 text-start font-sans text-xs font-medium leading-[1.5] text-paper transition-opacity duration-150 motion-reduce:delay-0 motion-reduce:transition-none",
          active ? "opacity-100 delay-300" : "opacity-0 delay-0",
          className,
        )}
      >
        {content}
      </span>, document.body) : null}
    </span>
  );
}
